import { classNames } from "@/lib/utils";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import styles from "./StockCommodityMovesChart.module.scss";
import { Stock } from "@/models/stock";
import { getChartDataNew } from "@/controllers/chart-data";
import { getStockCommodityMoves } from "@/controllers/stock-commodity";
import TimeSeriesChart, { type TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";

interface StockCommodityMovesChartProps {
    stock: Stock;
    /** Commodities the user added from the table. */
    extraSeries?: TimeSeries[];
}

/** The stock against the oil benchmark (and any commodity added from the table), volume below. */
const StockCommodityMovesChart = ({ stock, extraSeries }: StockCommodityMovesChartProps) => {
    const [bars, setBars] = useState<number[][]>([]);
    const [commodities, setCommodities] = useState<Record<string, [number, number][]>>({});
    const [obfuscated, setObfuscated] = useState(false);
    const [tsShortened, setTSShortened] = useState(false);

    useEffect(() => {
        let alive = true;
        Promise.all([getChartDataNew(stock.tradingitem_id, "ohlcv", dayjs().subtract(5, "years").format("YYYY-MM-DD")), getStockCommodityMoves("CL1")]).then(([stockData, commodityData]) => {
            if (!alive) return;
            setBars(stockData ?? []);
            setCommodities((commodityData?.index as Record<string, [number, number][]>) ?? {});
            setObfuscated(Boolean(commodityData?.obfuscated));
            setTSShortened(Boolean(commodityData?.ts_shortened));
        });
        return () => {
            alive = false;
        };
    }, [stock.stock_id]);

    const series = useMemo<TimeSeries[]>(
        () => [
            { id: "price", title: stock.ticker, type: "line", data: bars.map((r) => [r[0], r[4]] as [number, number]), precision: 2 },
            ...Object.entries(commodities).map<TimeSeries>(([name, data]) => ({ id: name, title: name, type: "line", data, axis: "left", precision: 2 })),
            ...(extraSeries ?? []).filter((e) => !(e.id in commodities)),
            { id: "volume", title: "Volume", type: "column", data: bars.map((r) => [r[0], r[r.length - 1]] as [number, number]), color: "dodgerblue", pane: "volume", format: "compact" },
        ],
        [bars, commodities, extraSeries, stock.ticker]
    );

    return (
        <div className={classNames(styles["chart-container"], obfuscated && !tsShortened && "obfuscated")}>
            <TimeSeriesChart series={series} paneHeights={{ volume: 0.35 }} initialRange="1y" exportName={`${stock.ticker}-commodities`} />
        </div>
    );
};

export default StockCommodityMovesChart;
